import { useT } from "@/context/LanguageContext";
import { translations } from "@/data/translations";
import { motion } from "framer-motion";
import {
  Zap,
  LayoutList,
  BookmarkCheck,
  Timer,
  Bell,
  BarChart3,
  Smartphone,
  FileText,
} from "lucide-react";

const iconMap = {
  Zap,
  LayoutList,
  BookmarkCheck,
  Timer,
  Bell,
  BarChart3,
  Smartphone,
  FileText,
};

export default function Features() {
  const t = useT(translations.features);

  return (
    <section
      id="features"
      data-testid="features-section"
      className="relative py-20 md:py-32"
    >
      {/* Background texture */}
      <div className="absolute inset-0 bg-[#1a1f26]">
        <div
          className="absolute inset-0 opacity-100"
          style={{
            backgroundImage:
              "linear-gradient(to right, #80808008 1px, transparent 1px), linear-gradient(to bottom, #80808008 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-medium tracking-[0.3em] uppercase text-[#b87b56]">
            {t.caption}
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mt-3"
            style={{ fontFamily: "Oswald, sans-serif" }}
          >
            {t.title}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {t.items.map((item, i) => {
            const Icon = iconMap[item.icon];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                data-testid={`feature-card-${i}`}
                className="bg-[#39424F] border border-white/5 p-8 hover:border-[#b87b56]/50 transition-colors group relative overflow-hidden"
              >
                {/* Hover glow */}
                <div className="absolute inset-0 bg-[#b87b56]/0 group-hover:bg-[#b87b56]/5 transition-colors" />

                <div className="relative z-10">
                  <div className="w-10 h-10 flex items-center justify-center mb-5 text-[#b87b56]">
                    {Icon && <Icon size={24} strokeWidth={1.5} />}
                  </div>
                  <h3
                    className="text-base font-bold text-white uppercase tracking-wider mb-2"
                    style={{ fontFamily: "Oswald, sans-serif" }}
                  >
                    {item.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-slate-400">
                    {item.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
